import Grid from '@mui/material/Grid';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import colors from '/@/components/theme-provider/colors';
import { _ } from '/@/locale';
import useWindowSize from '/@/utils/hooks/window-size';
import Title from '/@/v3/components/title';

/**
 * @typedef {typeof import('components/crop-monitoring/assets/graphicData').transformData} TransformDataFunc
 *
 * @param {{
 * dataGraphic: ReturnType<TransformDataFunc>
 * type: Parameters<TransformDataFunc>[1]
 * }}
 */
const Graphic = ({
    dataGraphic,
    type,
    width = '100%',
    height = 250,
    top = 20,
    bottom = 0,
    showXAxis = true,
    domain = [0, 100],
}) => {
    const windowWidth = useWindowSize().width;
    const isMobile = windowWidth <= 900;
    const isDesktopSm = windowWidth <= 1024;
    return (
        <ResponsiveContainer width={width} height={height}>
            <ComposedChart
                width={width}
                height={height}
                data={dataGraphic}
                margin={{ top: top, right: 15, left: 15, bottom: bottom }}
            >
                <CartesianGrid strokeDasharray='1 1' vertical={false} />
                {showXAxis && (
                    <XAxis
                        dataKey='data'
                        tickLine={false}
                        axisLine={false}
                        interval={
                            isMobile
                                ? Math.round(dataGraphic.length / 5)
                                : isDesktopSm
                                  ? Math.round(dataGraphic.length / 10)
                                  : Math.round(dataGraphic.length / 20)
                        }
                        style={{
                            fontSize: '10px',
                            fontWeight: 'bold',

                            color: colors.gray.default,
                        }}
                    />
                )}

                <YAxis
                    dataKey='dataToolTip'
                    tickLine={false}
                    axisLine={false}
                    interval='preserveStartEnd'
                    type='number'
                    domain={domain}
                    width={25}
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',

                        color: colors.gray.default,
                    }}
                />
                <YAxis
                    yAxisId='right'
                    tickLine={false}
                    axisLine={false}
                    orientation='right'
                    interval='preserveStartEnd'
                    dataKey='precipitations'
                    allowDecimals={false}
                    width={25}
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',

                        color: colors.gray.default,
                    }}
                />

                <CartesianGrid horizontal={false} vertical={false} strokeDasharray='3 3' />
                <Bar dataKey={type} barSize={20} fill={colors.themes.cultivio.primary.default} />
                <Tooltip payload={dataGraphic} content={<CustomTooltip type={type} />} />
                <Line
                    type='monotone'
                    yAxisId='right'
                    dataKey='precipitations'
                    stroke='#7AB3FF'
                    fillOpacity={1}
                    fill='url(#colorPv)'
                    strokeWidth={2}
                    dot={false}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default Graphic;

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <Grid
                container
                sx={{
                    borderRadius: 2,
                    border: '0.5px solid #E1E1E1',
                    py: 1,
                    backgroundColor: 'white!important',
                    width: 100,
                }}
            >
                <Grid item xs={12} sx={{ borderBottom: '0.5px solid #E1E1E1', p: 1 }}>
                    <Grid container alignItems='center' flexDirection='row' sx={{ gap: 1 }}>
                        <Grid item>
                            <span style={{ fontSize: '14px' }} className='material-icons'>
                                calendar_month
                            </span>
                        </Grid>
                        <Grid item>
                            <Title size='sm' bolder>
                                {Boolean(payload.length) && payload[0]['payload']['textTooltip']}
                            </Title>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid
                            item
                            container
                            xs={12}
                            sx={{ pt: 1, px: 1 }}
                            direction='row'
                            alignItems='center'
                            spacing={1}
                        >
                            <Grid item>
                                <div style={{ height: 5, width: 5, backgroundColor: '#7AB3FF', borderRadius: '50%' }} />
                            </Grid>
                            <Grid item>
                                <Title size='sm' bolder>
                                    {_('PA:')}
                                </Title>
                            </Grid>
                            <Grid item>
                                <div style={{ color: '#7AB3FF', fontWeight: 600 }}>
                                    {Boolean(payload.length) && payload[0]['payload']['precipitations'].toFixed(1)}
                                </div>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            container
                            xs={12}
                            sx={{ pt: 1, px: 1 }}
                            direction='row'
                            alignItems='center'
                            spacing={1}
                        >
                            <Grid item>
                                <div style={{ height: 5, width: 5, backgroundColor: '#006A28', borderRadius: '50%' }} />
                            </Grid>
                            <Grid item>
                                <Title size='sm' bolder>
                                    {_('PD:')}
                                </Title>
                            </Grid>
                            <Grid item>
                                <div style={{ color: '#006A28', fontWeight: 600 }}>
                                    {Boolean(payload.length) && payload[0]['payload']['rain']}
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return null;
};
